import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Clock } from "lucide-react";
import { PageHero, Reveal, Eyebrow, usePageMeta } from "@/components/kit";
import { HERO_BG } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

export default function BlogPage() {
  usePageMeta(
    "Blog | Redeemer Solutions LLC — Christiana, TN",
    "Guidance on security camera service, access control, fiber optics, and low-voltage care from Redeemer Solutions LLC in Christiana, Tennessee."
  );
  const [articles, setArticles] = useState(null);
  useEffect(() => {
    axios.get(`${API}/articles`).then((r) => setArticles(r.data)).catch(() => setArticles([]));
  }, []);

  return (
    <div data-testid="blog-page">
      <PageHero
        testid="blog-hero"
        eyebrow="The Blog"
        lines={["Field Notes &", { t: "Guidance", gold: true }]}
        sub="Practical wisdom on keeping your security and low-voltage systems healthy — from the field, in plain language."
        image={HERO_BG}
        imageAlt="Redeemer Solutions blog"
      />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
        {articles === null ? (
          <p className="text-zinc-500 text-sm py-10" data-testid="blog-loading">Loading articles…</p>
        ) : articles.length === 0 ? (
          <Reveal>
            <div className="border border-gold/15 bg-[#121216] rounded-sm p-10 text-center" data-testid="blog-empty">
              <Eyebrow className="mb-3">Coming Soon</Eyebrow>
              <p className="text-zinc-300 text-lg">Fresh articles are on the way — check back soon.</p>
            </div>
          </Reveal>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-article-list">
            {articles.map((a, i) => (
              <Reveal key={a.external_id} delay={i * 0.06}>
                <Link to={`/blog/${a.slug}`} data-testid={`blog-article-card-${i}`} className="group block h-full bg-[#121216] border border-gold/15 rounded-sm overflow-hidden transition-colors hover:border-gold/40">
                  {a.image_url && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={a.image_url} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                    </div>
                  )}
                  <div className="p-6">
                    {a.category && <Eyebrow className="mb-3">{a.category}</Eyebrow>}
                    <h2 className="font-display text-xl font-semibold text-[#F4EFE6] leading-snug group-hover:text-gold-champagne transition-colors">{a.title}</h2>
                    {a.subtitle && <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{a.subtitle}</p>}
                    <p className="flex flex-wrap items-center gap-3 text-xs text-zinc-500 mt-4">
                      {a.author_name && <span>{a.author_name}</span>}
                      {a.published_at && <span>{fmtDate(a.published_at)}</span>}
                      {a.read_time && <span className="inline-flex items-center gap-1"><Clock size={12} /> {a.read_time}</span>}
                    </p>
                    <p className="inline-flex items-center gap-2 text-gold text-sm mt-4">Read Article <ArrowRight size={14} /></p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
